import { useCallback, useEffect, useRef, useState } from 'react';
import { Inviter, Registerer, RegistererState, SessionState, UserAgent, Web } from 'sip.js';
import type { Invitation, Session } from 'sip.js';
import { signalSoedineniya } from './signalSoedineniya';
import { vzyatSvoyNabor } from './svoyNabor';
import { zvonokVhodyashchego } from './zvonokVhodyashchego';
import type { WorkplaceCredentials } from './api';
import { monoAudioModifier } from './sdpMono';
import { RegistratorPoOdnomu } from './sipRegistrator';
import { ProxiedWebSocketTransport } from './sipTransport';
import { soobshchitObObryve } from './workplaceApi';

import { godenNaLiniyu } from './nomerNabora';
import { tekstOtkazaLinii } from './otkazLinii';
/**
 * Телефон в браузере: регистрация у движка через WebSocket и один исходящий.
 *
 * Библиотека — sip.js (свой SIP-стек в браузере не пишем: это протокол, где
 * ошибка стоит немого звонка, а не исключения). Используется НЕ обёртка
 * SimpleUser, а базовый слой UserAgent/Inviter — по одной причине: нам нужен
 * звук РАНЬШЕ ответа. Автоответчик 8-800 часто говорит на ранней медиа (183),
 * а SimpleUser подключает динамики только после «сняли трубку»; на такой
 * проверке владелец слышал бы тишину при исправной линии.
 *
 * Перевалочные адреса для голоса (TURN) приходят с сервера вместе с учёткой —
 * своих, а не чужих: сторонний STUN добавил бы зависимость от чужого хоста.
 * Пустой список законен и означает «звоним напрямую»: так было до tel1f, и
 * так остаётся у тех, кому прямой путь и без того доступен.
 */

/**
 * Раз в столько миллисекунд экран спрашивает у браузера, что происходит с
 * голосовым каналом. Ровно этих цифр не хватило, чтобы отличить «пакеты не
 * ушли» от «ушли, но ответа нет».
 */
const STATS_INTERVAL_MS = 2500;

/**
 * Что показать человеку, когда звонок идёт, а голосового канала нет. Причина
 * почти всегда одна и та же и лежит НЕ в телефонии: до сервера не доходит
 * голосовой поток — режет firewall, VPN или сеть, из которой звонят.
 */
const SILENT_CALL_HINT =
  'Звонок соединился, но голосовой канал не открылся — звука не будет ни в одну сторону. '
  + 'Обычно это сеть, из которой вы звоните: VPN, корпоративный прокси или закрытые порты.';

export type TransportState = 'off' | 'connecting' | 'connected' | 'failed';
export type CallState = 'idle' | 'dialing' | 'early' | 'talking' | 'ended';

export interface SipPhone {
  transport: TransportState;
  registered: boolean;
  callState: CallState;
  error: string | null;
  /**
   * `s729`: отказ линии на последний набор («занято», «отклонён») — та же строка, что в `error`,
   * но названная отдельно: это исход одной попытки, а не неисправность телефона. Гаснет со
   * следующим набором или по `sbrositOtkaz`.
   */
  otkaz?: string | null;
  sbrositOtkaz?: () => void;
  /** Уровень своего микрофона 0..1 — «меня слышно» до того, как ответят. */
  micLevel: number;
  /** Пришёл ли голосовой поток с сервера (счётчик «слышно ли мне»). */
  remoteAudio: boolean;
  /** Открылся ли голосовой канал вообще. false на идущем звонке = немой звонок. */
  mediaOk: boolean;
  /**
   * Взял ли браузер на себя подавление эха — ФАКТ от самой дорожки, а не наша
   * просьба. `null` — пока неизвестно (звонок не начался).
   *
   * Вычитать эхо можно только там, где известны оба звука сразу: что играет в
   * динамики и что слышит микрофон. Это знает браузер оператора, и никто
   * больше — до сервера доходит уже смешанное. Просьбу мы ставим явно, но
   * часть устройств (обычно Android) её не выполняет и молчит об этом; тогда
   * собеседник слышит сам себя, и знать об этом должен оператор, а не мы
   * задним числом по записи.
   */
  echoCancelled: boolean | null;
  log: string[];
  /**
   * Когда разговор начался, миллисекундами. Отсюда считается таймер окна
   * разговора: момент знает тот, кто его поймал у движка, а не экран.
   */
  talkStartedAt: number | null;
  /** Микрофон выключен — клиент не слышит менеджера (карта 2.4). */
  muted: boolean;
  /** Разговор на удержании: клиенту играет музыка движка (§1.17). */
  onHold: boolean;
  connect: (credentials: WorkplaceCredentials) => Promise<void>;
  disconnect: () => Promise<void>;
  call: (number: string) => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => void;
  toggleHold: () => Promise<void>;
  /** Слепой перевод на внешний номер (§2.21). */
  transferTo: (number: string) => Promise<void>;
  /** Тональный набор в идущем разговоре: «нажмите 1», добавочный номер (`s726`). */
  sendDtmf: (tone: string) => boolean;
  /**
   * `s766`: последний вызов, пришедший в этот браузер от станции, — по нему всплывает окно звонка.
   * `svoy` — человек сам только что нажал «Позвонить» (тогда это не «входящий» и окна не будет).
   */
  vhod?: VhodVyzova | null;
}

export interface VhodVyzova { seq: number; at: number; svoy: boolean }

export function useSipPhone(audioElRef: React.RefObject<HTMLAudioElement | null>): SipPhone {
  const [transport, setTransport] = useState<TransportState>('off');
  const [registered, setRegistered] = useState(false);
  // s734: сторожу подключения нужен текущий флаг, а не тот, что был при его создании.
  const registeredRef = useRef(false);
  useEffect(() => {
    registeredRef.current = registered;
  }, [registered]);
  const [vhod, setVhod] = useState<VhodVyzova | null>(null);
  const vhodSeqRef = useRef(0);
  const [callState, setCallState] = useState<CallState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [otkaz, setOtkaz] = useState<string | null>(null);
  const otkazRef = useRef<string | null>(null);
  const [micLevel, setMicLevel] = useState(0);
  const [remoteAudio, setRemoteAudio] = useState(false);
  const [mediaOk, setMediaOk] = useState(false);
  const [echoCancelled, setEchoCancelled] = useState<boolean | null>(null);
  const mediaOkRef = useRef(false);
  const [log, setLog] = useState<string[]>([]);
  const [talkStartedAt, setTalkStartedAt] = useState<number | null>(null);
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const holdRef = useRef(false);

  const uaRef = useRef<UserAgent | null>(null);
  /**
   * `s726`: человек подключал телефон и сам его не отключал. Пока это так, оборвавшаяся связь
   * с сервером поднимается сама — раньше после моргнувшей сети телефон молча оставался
   * отключённым, а статус на сервере — «Готов».
   */
  const wantedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const registererRef = useRef<Registerer | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const domainRef = useRef<string>('');

  const audioCtxRef = useRef<AudioContext | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const statsTimerRef = useRef<number | null>(null);

  const pushLog = useCallback((line: string) => {
    const stamp = new Date().toLocaleTimeString('ru-RU', { hour12: false });
    setLog((prev) => [`${stamp}  ${line}`, ...prev].slice(0, 60));
  }, []);

  const stopMeter = useCallback(() => {
    if (statsTimerRef.current !== null) {
      window.clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    }

    if (meterRafRef.current !== null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    setMicLevel(0);
  }, []);

  /** Полоска своего голоса: считается по тому же треку, который уходит в линию. */
  const startMeter = useCallback((track: MediaStreamTrack) => {
    stopMeter();

    const ctx = new AudioContext();
    audioCtxRef.current = ctx;

    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    const buffer = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteTimeDomainData(buffer);

      let peak = 0;
      for (const value of buffer) {
        peak = Math.max(peak, Math.abs(value - 128) / 128);
      }

      setMicLevel(peak);
      meterRafRef.current = requestAnimationFrame(tick);
    };

    tick();
  }, [stopMeter]);

  /**
   * Что на самом деле происходит с голосовым каналом — цифрами браузера.
   *
   * ⭐ Без этого «звонок идёт, звука нет» неразличимо изнутри: непонятно, ушли
   * ли вообще пакеты, ответил ли сервер, каким путём пошёл голос. Пишем в
   * журнал экрана короткой строкой: выбранная пара адресов, сколько запросов
   * ушло и сколько ответов вернулось, сколько байт голоса прошло.
   */
  const startStatsWatch = useCallback((pc: RTCPeerConnection) => {
    window.clearInterval(statsTimerRef.current ?? undefined);

    let lastLine = '';

    const poll = () => {
      if (pc.connectionState === 'closed') {
        window.clearInterval(statsTimerRef.current ?? undefined);
        statsTimerRef.current = null;

        return;
      }

      void pc.getStats().then((report) => {
        const candidates = new Map<string, { type?: string; address?: string; port?: number; protocol?: string }>();
        const pairs: RTCIceCandidatePairStats[] = [];
        let selectedId: string | undefined;

        report.forEach((entry) => {
          if (entry.type === 'candidate-pair') {
            pairs.push(entry as RTCIceCandidatePairStats);
          }

          // Браузеры помечают выбранную пару по-разному: Chrome — ссылкой из
          // transport, Firefox — полем `selected`. Смотрим оба, иначе именно
          // в нужный момент наблюдатель окажется слеп.
          if (entry.type === 'transport') {
            selectedId = (entry as RTCTransportStats).selectedCandidatePairId;
          }

          if (entry.type === 'local-candidate' || entry.type === 'remote-candidate') {
            const c = entry as RTCIceCandidatePairStats & { candidateType?: string; address?: string; port?: number; protocol?: string };
            candidates.set(c.id, { type: c.candidateType, address: c.address, port: c.port, protocol: c.protocol });
          }
        });

        const pair = pairs.find((p) => p.id === selectedId)
          ?? pairs.find((p) => (p as RTCIceCandidatePairStats & { selected?: boolean }).selected)
          ?? pairs.find((p) => p.nominated)
          ?? pairs.find((p) => p.state === 'succeeded');

        if (!pair) {
          const sent = pairs.reduce((acc, p) => acc + (p.requestsSent ?? 0), 0);
          const got = pairs.reduce((acc, p) => acc + (p.responsesReceived ?? 0), 0);
          const line = `путь голоса ещё не выбран (проб ${pairs.length}, запросов ${sent}, ответов ${got})`;

          if (line !== lastLine) {
            lastLine = line;
            pushLog(line);
          }

          return;
        }

        const local = candidates.get(pair.localCandidateId ?? '');
        const remote = candidates.get(pair.remoteCandidateId ?? '');
        const way = local?.type === 'relay' || remote?.type === 'relay' ? 'через перевалочный' : 'напрямую';

        /*
         * ⭐ Голос идёт — это ПРИШЕДШИЕ БАЙТЫ, а не состояние соединения.
         *
         * Раньше признак ставился по `connectionState === 'connected'`, и на
         * телефоне владельца экран написал «голосовой канал не открылся» в
         * разговоре, где он прекрасно слышал робота, а робот — его. Состояние
         * соединения запаздывает (мобильная сеть, VPN, перевалочный сервер), а
         * байты либо идут, либо нет. Тот же признак считает и сторож линии
         * (repro tel1e: rtp_audio_in_packet_count).
         */
        if ((pair.bytesReceived ?? 0) > 0 && !mediaOkRef.current) {
          mediaOkRef.current = true;
          setMediaOk(true);
          pushLog('звук пошёл: пакеты от сервера приходят');
        }

        const line = `путь голоса ${way} (${local?.type ?? '?'}/${local?.protocol ?? '?'} → ${remote?.address ?? '?'}:${remote?.port ?? '?'}), `
          + `запросов ${pair.requestsSent ?? 0}, ответов ${pair.responsesReceived ?? 0}, `
          + `байт ${pair.bytesSent ?? 0}/${pair.bytesReceived ?? 0}`;

        if (line !== lastLine) {
          lastLine = line;
          pushLog(line);
        }
      });
    };

    poll();
    statsTimerRef.current = window.setInterval(poll, STATS_INTERVAL_MS);
  }, [pushLog]);

  /**
   * Динамики подключаются на КАЖДЫЙ пришедший трек, а не по факту ответа —
   * ровно ради ранней медиа (см. шапку файла).
   */
  const attachMedia = useCallback((session: Session) => {
    session.delegate = {
      onSessionDescriptionHandler: (handler) => {
        const pc = (handler as Web.SessionDescriptionHandler).peerConnection;

        if (!pc) {
          return;
        }

        const remoteStream = new MediaStream();

        pc.addEventListener('track', (event) => {
          if (event.track.kind !== 'audio') {
            return;
          }

          remoteStream.addTrack(event.track);
          setRemoteAudio(true);
          pushLog('пришёл голосовой поток от сервера');

          const element = audioElRef.current;

          if (element) {
            // Динамик включается ЯВНО: заглушённый элемент — самая тихая из
            // возможных поломок, её не видно ни в логах, ни на экране.
            element.muted = false;
            element.volume = 1;
            element.srcObject = remoteStream;
            void element.play().catch((e: unknown) => {
              pushLog(`браузер не дал включить звук: ${describe(e)}`);
            });
          }
        });

        // ⭐ Почему НЕ собрался перевалочный адрес — браузер знает и молчит,
        // пока не спросишь. Именно этого не хватило, чтобы понять, пустил ли
        // прокси наш перевалочный сервер: снаружи было видно только «нет
        // relay-адреса», без причины.
        pc.addEventListener('icecandidateerror', (event) => {
          const e = event as RTCPeerConnectionIceErrorEvent;

          pushLog(`адрес не собрался: ${e.errorCode} ${e.errorText || ''} ${e.url ?? ''}`.trim());
        });

        pc.addEventListener('connectionstatechange', () => {
          pushLog(`голосовой канал: ${translateChannel(pc.connectionState)}`);

          if (pc.connectionState === 'connected') {
            mediaOkRef.current = true;
            setMediaOk(true);

            const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');

            if (sender?.track) {
              startMeter(sender.track);

              /*
               * Просьба и факт — разные вещи: браузер вправе не выполнить
               * условие и промолчать. Спрашиваем у самой дорожки, что вышло,
               * и пишем в журнал страницы — иначе «эхо не подавляется»
               * приходится доказывать сравнением дорожек постфактум.
               */
              const applied = sender.track.getSettings?.() ?? {};

              setEchoCancelled(applied.echoCancellation === true);

              pushLog(
                `микрофон: подавление эха ${applied.echoCancellation ? 'включено' : 'ВЫКЛЮЧЕНО'}`
                + `, шумодав ${applied.noiseSuppression ? 'включён' : 'выключен'}`,
              );
            }
          }

          if (pc.connectionState === 'failed') {
            setError(SILENT_CALL_HINT);
          }
        });

        // ⭐ Сторож немого звонка (tel1f). Разговор может числиться идущим, а
        // голосового канала не быть вовсе: браузер не достучался до сервера.
        // Именно так это и выглядело у владельца — «разговор идёт» на экране и
        // тишина в наушниках. Экран обязан называть это сам, а не молчать.
        // Восемь секунд не хватало мобильной сети с VPN: путь голоса ещё
        // выбирался, звук уже шёл, а экран уже пугал. Ждём вдвое дольше и
        // судим по тем же байтам, что и признак выше.
        window.setTimeout(() => {
          if (!mediaOkRef.current && pc.connectionState !== 'closed') {
            setError(SILENT_CALL_HINT);
            pushLog('голосовой канал так и не открылся');
          }
        }, 16000);

        startStatsWatch(pc);
      },
    };
  }, [audioElRef, pushLog, startMeter, startStatsWatch]);

  const connect = useCallback(async (credentials: WorkplaceCredentials) => {
    setError(null);
    setTransport('connecting');
    domainRef.current = credentials.sip_domain;

    try {
      const uri = UserAgent.makeURI(`sip:${credentials.username}@${credentials.sip_domain}`);

      if (!uri) {
        throw new Error(`не удалось собрать SIP-адрес из «${credentials.username}@${credentials.sip_domain}»`);
      }

      const ua = new UserAgent({
        uri,
        displayName: credentials.display_name,
        authorizationUsername: credentials.username,
        authorizationPassword: credentials.password,
        transportConstructor: ProxiedWebSocketTransport,
        /*
         * s734 (жалоба 09.10 12:05, та же, что 07.10: «сам переходит в Недоступен»): в простое
         * соединение с сервером молчит минутами — между продлениями регистрации по нему не идёт
         * ничего, и сеть по дороге его закрывает (журнал сервера 09.10: обрывы у одного браузера
         * в 11:23, 11:39, 11:53, 12:03). Раз в ~25 секунд шлём пустую строку — соединение живое.
         */
        transportOptions: { server: credentials.ws_url, keepAliveInterval: 25 },
        logLevel: 'error',
        /*
         * ⭐ Входящий вызов принимается САМ (§2.2, инцидент 13.08).
         *
         * Без этого браузер отвечал «занято» на каждый входящий INVITE — и
         * обзвон обрывался в худший момент: клиенту дозвонились, он снял
         * трубку, а соединить его с оператором не удалось. Именно так движок
         * и соединяет прогрессивный обзвон: набирает клиента, а оператору
         * звонит входящим в кабинет.
         *
         * Приём автоматический — по ТЗ §2.2 это поведение по умолчанию:
         * оператор в статусе «Готов» уже согласился принимать звонки, и
         * заставлять его жать «ответить» на каждый — терять секунды разговора.
         */
        delegate: {
          onInvite: (invitation: Invitation) => {
            /*
             * s734 (бой 08.10 10:33 и 09.10 10:15): второй вызов при идущем разговоре НЕ принимается.
             *
             * Станция по ошибке учёта сочла менеджера свободным и позвонила ему вторым звонком.
             * Браузер снял трубку сам, звук переключился на новую клиентку, а первая осталась на
             * линии в тишине — ни услышать её, ни положить трубку менеджер уже не мог. Что бы ни
             * решила станция, разговор не прерывается: второму вызову — «занято».
             */
            const idushchiy = sessionRef.current;
            if (
              idushchiy
              && (idushchiy.state === SessionState.Establishing || idushchiy.state === SessionState.Established)
            ) {
              pushLog('второй входящий при идущем разговоре — отклонён');
              void invitation.reject({ statusCode: 486 }).catch(() => undefined);
              return;
            }

            sessionRef.current = invitation;
            attachMedia(invitation);

            /*
             * s766 (обращение техподдержки №4): вызов пришёл — менеджер узнаёт об этом СРАЗУ.
             * Чужой вызов (клиент, обзвон, автоперезвон) звучит звонком и поднимает окно на любой
             * странице; вызов, который человек начал сам кнопкой «Позвонить», — прежним знаком
             * соединения и без окна.
             */
            const svoy = vzyatSvoyNabor();
            vhodSeqRef.current += 1;
            setVhod({ seq: vhodSeqRef.current, at: Date.now(), svoy });
            if (!svoy) zvonokVhodyashchego();

            invitation.stateChange.addListener((state) => {
              switch (state) {
                case SessionState.Established:
                  setCallState('talking');
                  setTalkStartedAt(Date.now());
                  pushLog('входящий соединён');
                  // s732: трубку браузер снял сам — менеджер должен это УСЛЫШАТЬ.
                  // s766: у чужого вызова это уже сказал звонок — два сигнала подряд не нужны.
                  if (svoy) signalSoedineniya();
                  break;
                case SessionState.Terminated:
                  setCallState('ended');
                  setTalkStartedAt(null);
                  stopMeter();
                  if (sessionRef.current === invitation) sessionRef.current = null;
                  pushLog('входящий завершён');
                  break;
                default:
                  break;
              }
            });

            setCallState('dialing');
            pushLog('входящий вызов — принимаю');

            /*
             * ⭐ Правку SDP к ОТВЕТУ не применяем (13.08).
             *
             * Моно-модификатор писался под наше предложение исходящего
             * звонка; на ответе входящему он ломал приём, и браузер молча не
             * брал трубку — звонок робота уходил в тишину. Один канал на
             * входящем и так обеспечен: предложение делает движок, а он у нас
             * настроен на моно.
             */
            void invitation
              .accept({
                sessionDescriptionHandlerOptions: {
                  constraints: { audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                  }, video: false },
                },
              })
              .catch((e: unknown) => {
                setError('Не удалось принять звонок: ' + describe(e));
                pushLog(`входящий не принят: ${describe(e)}`);
              });
          },
        },
        sessionDescriptionHandlerFactoryOptions: {
          // Перевалочные адреса приходят с сервера вместе с учёткой. Пустой
          // список — законно: звоним напрямую, как до появления TURN.
          peerConnectionConfiguration: { iceServers: credentials.ice_servers },
          // s726: не ждём сбора всех адресов голоса до пяти секунд (значение библиотеки по умолчанию).
          // Клиентка уже сняла трубку, а менеджер подключался через 2–5 секунд и слышал тишину
          // (жалоба менеджера 07.10). Секунды хватает на прямые и перевалочные адреса.
          iceGatheringTimeout: 1000,
          constraints: { audio: {
              // ⭐ Подавление эха просим ЯВНО (находка приёмки 12.08).
              //
              // Оператор работает микрофоном ноутбука и слушает динамики —
              // микрофон при этом слышит собеседника, и запись получает его
              // голос дважды: разница между дорожками у владельца вышла
              // −73 дБ, то есть они совпали. Браузер умеет вычитать из
              // микрофона то, что сам играет в динамики, но без явного
              // условия делает это не всегда (Android — чаще нет).
              //
              // Шумодав и авторегулировка усиления идут следом: они из того же
              // набора «разговор, а не запись концерта» и на распознавание
              // влияют в ту же сторону.
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }, video: false },
        },
      });

      const podtverzhdenie = { requestDelegate: { onAccept: () => setRegistered(true) } };

      const vosstanovit = (popytka: number) => {
        if (!wantedRef.current || uaRef.current !== ua) return;

        window.clearTimeout(reconnectTimerRef.current ?? undefined);
        reconnectTimerRef.current = window.setTimeout(() => {
          if (!wantedRef.current || uaRef.current !== ua) return;

          setTransport('connecting');
          void ua.reconnect()
            .then(async () => {
              setTransport('connected');
              pushLog('связь с сервером восстановлена');
              await registererRef.current?.register(podtverzhdenie);
            })
            .catch(() => {
              setTransport('failed');
              vosstanovit(popytka + 1);
            });
        }, Math.min(30_000, 2000 * 2 ** Math.min(popytka, 4)));
      };

      ua.transport.onDisconnect = (e?: Error) => {
        setTransport('failed');
        setRegistered(false);
        pushLog(`связь с сервером потеряна${e ? `: ${e.message}` : ''}`);
        // s734: причину обрыва знает только браузер — называем её серверу (журнал телефонии).
        void soobshchitObObryve({
          prichina: (e ? e.message : 'закрыто самой панелью').slice(0, 200),
          vkladka: document.visibilityState,
          set: navigator.onLine,
          razgovor: sessionRef.current !== null,
          zhilo_sek: Math.round((Date.now() - podklyuchenS) / 1000),
        }).catch(() => undefined);
        // Обрыв не по нашей воле — поднимаем сами: 2, 4, 8 … не чаще раза в полминуты.
        if (e) vosstanovit(0);
      };

      uaRef.current = ua;
      wantedRef.current = true;
      const podklyuchenS = Date.now();

      await ua.start();
      setTransport('connected');
      pushLog(
        credentials.ice_servers.length > 0
          ? `соединение с ${credentials.ws_url} установлено, перевалочные адреса голоса получены`
          : `соединение с ${credentials.ws_url} установлено, звоним напрямую`,
      );

      /*
       * s734 (бой 09.10: обрывы соединения телефона в 11:23, 11:39, 11:53, 12:03, 12:28, 12:43,
       * 12:50, 12:54, 12:57; в 12:57:58 — посреди разговора, трубку положить было нечем).
       *
       * Регистрация живёт 600 секунд, а библиотека по умолчанию продлевает её на 99 % срока — за
       * шесть секунд до конца. Вкладка в фоне будит свои таймеры раз в минуту: продление
       * опаздывало, станция считала регистрацию истёкшей и закрывала соединение сама. Продлеваем
       * на половине срока — опоздание в минуту больше ничего не стоит.
       */
      // Обращение №90: один REGISTER за раз — и наш, и плановое продление библиотеки (sipRegistrator.ts).
      const registerer = new RegistratorPoOdnomu(ua, { expires: 600, refreshFrequency: 50 });
      registererRef.current = registerer;

      registerer.stateChange.addListener((state) => {
        setRegistered(state === RegistererState.Registered);
        pushLog(`регистрация: ${translateRegisterState(state)}`);
      });

      await registerer.register({
        requestDelegate: {
          onReject: (response) => {
            setError(`движок отказал в регистрации: ${response.message.statusCode} ${response.message.reasonPhrase}`);
          },
        },
      });
    } catch (e: unknown) {
      setTransport('failed');
      setError(describe(e));
      pushLog(`не подключились: ${describe(e)}`);
    }
  }, [pushLog, attachMedia, stopMeter]);

  /*
   * s726: сторож подключения. Регистрация телефона пропадала сама через 5–10 минут простоя
   * (вкладка в фоне, сеть моргнула), а поднимали её только при явном обрыве соединения —
   * менеджер видел «Недоступен» при статусе «Готов» (жалоба 07.10). Раз в 15 секунд, при
   * возврате на вкладку и при возврате сети: нет соединения — поднимаем, нет регистрации —
   * регистрируемся. Во время разговора ничего не трогаем.
   */
  useEffect(() => {
    /*
     * s734 (бой 09.10 12:03–12:05): соединение оборвалось, телефон переподключился и на станции
     * был зарегистрирован — а панель до обновления страницы показывала «Недоступен». После
     * обрыва флаг регистрации гасится, а вернуть его было некому: библиотека считает себя
     * зарегистрированной и о повторной регистрации событием не сообщает. Флаг возвращает
     * ответ станции на регистрацию — и здесь, и при восстановлении связи после обрыва.
     */
    const podtverzhdenie = { requestDelegate: { onAccept: () => setRegistered(true) } };
    const proverit = () => {
      const ua = uaRef.current;
      const registerer = registererRef.current;
      if (!wantedRef.current || !ua || !registerer || sessionRef.current) return;

      if (!ua.transport.isConnected()) {
        void ua.reconnect().then(() => registerer.register(podtverzhdenie)).catch(() => undefined);
        return;
      }
      if (registerer.state !== RegistererState.Registered || !registeredRef.current) {
        void registerer.register(podtverzhdenie).catch(() => undefined);
      }
    };
    const naVid = () => { if (document.visibilityState === 'visible') proverit(); };
    const timer = window.setInterval(proverit, 15_000);
    document.addEventListener('visibilitychange', naVid);
    window.addEventListener('online', proverit);
    window.addEventListener('focus', proverit);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', naVid);
      window.removeEventListener('online', proverit);
      window.removeEventListener('focus', proverit);
    };
  }, []);

  const disconnect = useCallback(async () => {
    wantedRef.current = false;
    window.clearTimeout(reconnectTimerRef.current ?? undefined);
    stopMeter();

    try {
      await registererRef.current?.unregister();
    } catch {
      // Уже отвалились — отдельной беды в этом нет.
    }

    try {
      await uaRef.current?.stop();
    } catch {
      // То же самое: гасим молча, состояние всё равно сбрасываем ниже.
    }

    registererRef.current = null;
    uaRef.current = null;
    sessionRef.current = null;

    setRegistered(false);
    setTransport('off');
    setCallState('idle');
    setRemoteAudio(false);
    setMediaOk(false);
    mediaOkRef.current = false;
    pushLog('отключились');
  }, [pushLog, stopMeter]);

  const call = useCallback(async (number: string) => {
    const ua = uaRef.current;

    if (!ua) {
      setError('Сначала подключитесь.');
      return;
    }

    setError(null);
    otkazRef.current = null;
    setOtkaz(null);
    setRemoteAudio(false);
    setMediaOk(false);
    mediaOkRef.current = false;
    // Микрофон и пауза принадлежат разговору, а не человеку: новый звонок
    // начинается с включённого микрофона, иначе первый «алло» уйдёт в пустоту.
    setMuted(false);
    setOnHold(false);
    holdRef.current = false;
    setTalkStartedAt(null);

    // `s720`: последний рубеж — на линию уходят только цифры, прошедшие правило номера.
    if (!godenNaLiniyu(number)) {
      setError(`Номер «${number}» не принят: проверьте цифры. Звонок не начат.`);
      return;
    }

    const target = UserAgent.makeURI(`sip:${number}@${domainRef.current}`);

    if (!target) {
      setError(`Номер «${number}» не похож на номер.`);
      return;
    }

    const inviter = new Inviter(ua, target, {
      // Ранняя медиа: слушаем то, что линия отдаёт ДО ответа.
      earlyMedia: true,
      /*
       * ⭐ Просим голос ОДНИМ каналом (находка приёмки 12.08, sdpMono.ts).
       *
       * Браузер, попросивший стерео, забирает у записи разделение ролей: движок
       * пишет в файл стерео самого потока, и микрофон оператора в запись не
       * попадает вовсе. Так у владельца пропали все его слова, а слова
       * собеседника встали дважды.
       */
      sessionDescriptionHandlerModifiers: [monoAudioModifier],
      sessionDescriptionHandlerOptions: { constraints: { audio: {
              // ⭐ Подавление эха просим ЯВНО (находка приёмки 12.08).
              //
              // Оператор работает микрофоном ноутбука и слушает динамики —
              // микрофон при этом слышит собеседника, и запись получает его
              // голос дважды: разница между дорожками у владельца вышла
              // −73 дБ, то есть они совпали. Браузер умеет вычитать из
              // микрофона то, что сам играет в динамики, но без явного
              // условия делает это не всегда (Android — чаще нет).
              //
              // Шумодав и авторегулировка усиления идут следом: они из того же
              // набора «разговор, а не запись концерта» и на распознавание
              // влияют в ту же сторону.
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }, video: false } },
    });

    sessionRef.current = inviter;
    attachMedia(inviter);

    inviter.stateChange.addListener((state) => {
      switch (state) {
        case SessionState.Establishing:
          setCallState('dialing');
          break;
        case SessionState.Established:
          setCallState('talking');
          // Момент начала разговора ловим здесь, у самого движка: экран,
          // считающий «с тех пор как я увидел статус», разойдётся с тем,
          // что попадёт в звонок, а эти числа сверяются число-в-число (§6.1).
          setTalkStartedAt(Date.now());
          pushLog('разговор начался');
          break;
        case SessionState.Terminated:
          setCallState('ended');
          setTalkStartedAt(null);
          stopMeter();
          sessionRef.current = null;
          pushLog('звонок завершён');
          break;
        default:
          break;
      }
    });

    setCallState('dialing');
    pushLog(`набираю ${number}`);

    try {
      await inviter.invite({
        requestDelegate: {
          onProgress: (response) => {
            const code = response.message.statusCode;
            pushLog(`линия отвечает: ${code} ${response.message.reasonPhrase}`);

            if (code === 183) {
              setCallState('early');
            }
          },
          onReject: (response) => {
            // s726: 487 — ответ на НАШУ же отмену набора (человек положил трубку до ответа).
            // Это не отказ линии, красная строка «Отказ: 487 Request Terminated» тут ложная.
            if (response.message.statusCode === 487) {
              pushLog('набор отменён до ответа');
              return;
            }
            // `s729`: отказ линии называет номер и то, что ответил оператор; «Отказ: 486 Busy Here»
            // без номера читался как поломка рабочего места.
            const tekst = tekstOtkazaLinii(
              number,
              response.message.statusCode ?? 0,
              response.message.reasonPhrase ?? '',
              response.message.getHeader('X-Reason') ?? null,
            );
            otkazRef.current = tekst;
            setOtkaz(tekst);
            setError(tekst);
            pushLog(`линия отказала: ${response.message.statusCode} ${response.message.reasonPhrase}`);
          },
        },
      });
    } catch (e: unknown) {
      setCallState('ended');
      setError(describe(e));
      pushLog(`звонок не состоялся: ${describe(e)}`);
    }
  }, [attachMedia, pushLog, stopMeter]);

  /**
   * Микрофон. Дорожка глушится на месте, разговор не пересогласовывается:
   * повторный обмен предложениями ради «не слышно меня» — это риск уронить
   * живой звонок ради кнопки, которую жмут на секунду.
   */
  const toggleMute = useCallback(() => {
    const session = sessionRef.current;
    const pc = (session?.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined)?.peerConnection;

    if (!pc) return;

    let nowMuted = false;

    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !sender.track.enabled;
        nowMuted = !sender.track.enabled;
      }
    });

    setMuted(nowMuted);
    pushLog(nowMuted ? 'микрофон выключен' : 'микрофон включён');
  }, [pushLog]);

  /**
   * Удержание. Здесь пересогласование как раз нужно: музыку клиенту играет
   * ДВИЖОК, и узнать о паузе он может только из нового предложения (sendonly).
   * Глушить дорожку вместо этого означало бы тишину в трубке вместо музыки —
   * клиент кладёт трубку, решив, что связь оборвалась.
   */
  const toggleHold = useCallback(async () => {
    const session = sessionRef.current;

    if (!session || session.state !== SessionState.Established) return;

    const next = !holdRef.current;

    try {
      await session.invite({
        sessionDescriptionHandlerOptions: { hold: next },
        // Пересогласование — это новое предложение, и в нём стерео вернулось бы
        // само: браузер собирает SDP заново. Тогда разделение ролей ломалось бы
        // с середины разговора — ровно с той секунды, когда сняли паузу.
        sessionDescriptionHandlerModifiers: [monoAudioModifier],
      } as Parameters<Session['invite']>[0]);

      holdRef.current = next;
      setOnHold(next);
      pushLog(next ? 'пауза: клиенту играет музыка' : 'пауза снята');
    } catch (e: unknown) {
      pushLog(`пауза не получилась: ${describe(e)}`);
    }
  }, [pushLog]);

  /**
   * Слепой перевод (§2.21): отдаём разговор и уходим из него.
   *
   * Именно слепой, а не «дождаться ответа»: удерживать клиента, пока
   * менеджер договаривается с третьей стороной, — это тишина в трубке на
   * минуту. Клиенту при переводе играет музыка движка, у принявшего
   * всплывает карточка.
   */
  const transferTo = useCallback(async (number: string) => {
    const session = sessionRef.current;

    if (!session || session.state !== SessionState.Established) {
      setError('Переводить нечего: разговор не идёт.');
      return;
    }

    // `s720`: перевод — тем же правилом номера, что и набор.
    if (!godenNaLiniyu(number)) {
      setError(`Номер «${number}» не принят: проверьте цифры. Перевод не начат.`);
      return;
    }

    const target = UserAgent.makeURI(`sip:${number}@${domainRef.current}`);

    if (!target) {
      setError(`Номер «${number}» не похож на номер.`);
      return;
    }

    try {
      await session.refer(target);
      pushLog(`перевод на ${number}`);
    } catch (e: unknown) {
      setError(`Перевод не получился: ${describe(e)}`);
      pushLog(`перевод не получился: ${describe(e)}`);
    }
  }, [pushLog]);

  /**
   * Тональный набор (`s726`): цифра уходит собеседнику тем же голосовым каналом (RFC 2833) —
   * так её слышит и автоответчик «нажмите 1», и станция с добавочными номерами.
   */
  const sendDtmf = useCallback((tone: string): boolean => {
    const session = sessionRef.current;

    if (!session || session.state !== SessionState.Established || !/^[0-9*#]$/.test(tone)) {
      return false;
    }

    const handler = session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const ok = handler?.sendDtmf(tone, { duration: 160, interToneGap: 70 }) ?? false;

    pushLog(ok ? `кнопка ${tone} отправлена` : `кнопка ${tone} не ушла`);

    return ok;
  }, [pushLog]);

  const hangup = useCallback(async () => {
    const session = sessionRef.current;

    if (!session) {
      return;
    }

    try {
      if (session.state === SessionState.Established) {
        await session.bye();
      } else if (session instanceof Inviter) {
        await session.cancel();
      } else if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
        /*
         * ⭐ Входящий звонок кладут ОТКАЗОМ, а не отменой (13.08).
         *
         * `cancel()` есть только у исходящего; у входящего, который ещё не
         * принят, — `reject()`. Без этой ветки кнопка «Завершить» на звонке
         * обзвона не делала ничего: владелец нажимал, а разговор висел.
         */
        await (session as Invitation).reject();
      }
    } catch (e: unknown) {
      pushLog(`не удалось положить трубку: ${describe(e)}`);
    }

    stopMeter();
  }, [pushLog, stopMeter]);

  // Вкладку закрыли/ушли со страницы — регистрация не должна пережить экран:
  // иначе движок считает место доступным, а слушать некому.
  useEffect(() => () => {
    wantedRef.current = false;
    window.clearTimeout(reconnectTimerRef.current ?? undefined);
    stopMeter();
    void uaRef.current?.stop();
  }, [stopMeter]);

  // `s729`: строка отказа пережила свою попытку — человек её увидел и пошёл дальше.
  const sbrositOtkaz = useCallback(() => {
    const tekst = otkazRef.current;

    if (tekst === null) return;

    otkazRef.current = null;
    setOtkaz(null);
    setError(prev => (prev === tekst ? null : prev));
  }, []);

  return {
    transport,
    registered,
    callState,
    error,
    otkaz,
    sbrositOtkaz,
    micLevel,
    remoteAudio,
    mediaOk,
    echoCancelled,
    log,
    talkStartedAt,
    muted,
    onHold,
    connect,
    disconnect,
    call,
    hangup,
    toggleMute,
    toggleHold,
    transferTo,
    sendDtmf,
    vhod,
  };
}

function describe(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }

  return String(e);
}

function translateChannel(state: RTCPeerConnectionState): string {
  switch (state) {
    case 'new': return 'создаётся';
    case 'connecting': return 'договаривается';
    case 'connected': return 'открыт, звук идёт';
    case 'disconnected': return 'потерян';
    case 'failed': return 'не открылся';
    case 'closed': return 'закрыт';
    default: return String(state);
  }
}

function translateRegisterState(state: RegistererState): string {
  switch (state) {
    case RegistererState.Initial:
      return 'ещё не начиналась';
    case RegistererState.Registered:
      return 'принята движком';
    case RegistererState.Unregistered:
      return 'снята';
    case RegistererState.Terminated:
      return 'закрыта';
    default:
      return String(state);
  }
}
